import React, { useState, useEffect } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop, { EventInteractionArgs } from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Add from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./styles.css";

import AddAppointmentDialog, { AppointmentData } from "./AddAppointmentDialog";
import dayjs, { Dayjs } from "dayjs";
import { CalendarIconTrigger } from "./CalendarIconTrigger";
export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  eventType?: string;
  caseId?: string;
  facilitator?: string;
  putativeParent?: string;
  participants?: string[];
  meetingMethod?: string;
  labName?: string;
  hearingType?: string;
  judge?: string;
  courtAddress?: string;
  courtCity?: string;
  courtState?: string;
  courtRoom?: string;
  notes?: string;
}

export interface SchedularProps {
  events?: CalendarEvent[];
  defaultView?: "month" | "week";
  selectable?: boolean;
  onAppointmentSave?: (data: AppointmentData) => void;
  AppointmentDialogComponent?: React.FC<{
    open: boolean;
    onClose: () => void;
    onSave: (data: AppointmentData) => void;
    initialData?: AppointmentData | null;
  }>;
  eventPropGetter?: (event: CalendarEvent) => { style?: React.CSSProperties; className?: string };
}

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DragAndDropCalendar = withDragAndDrop<CalendarEvent>(Calendar);

const combineDayjsToDate = (day: Dayjs | null): Date => (day ? day.toDate() : new Date());

const mapCalendarEventToAppointment = (event: CalendarEvent | null): AppointmentData | null => {
  if (!event) return null;
  return {
    id: event.id,
    date: dayjs(event.start),
    startTime: dayjs(event.start),
    endTime: dayjs(event.end),
    appointmentType: event.eventType as any,
    caseId: event.caseId,
    facilitator: event.facilitator,
    putativeParent: event.putativeParent,
    participants: event.participants,
    meetingMethod: event.meetingMethod,
    labName: event.labName,
    hearingType: event.hearingType,
    judge: event.judge,
    courtAddress: event.courtAddress,
    courtCity: event.courtCity,
    courtState: event.courtState,
    courtRoom: event.courtRoom,
    notes: event.notes,
  };
};

const Schedular: React.FC<SchedularProps> = ({
  events = [],
  defaultView = "month",
  selectable = true,
  onAppointmentSave,
  AppointmentDialogComponent = AddAppointmentDialog,
  eventPropGetter: externalEventPropGetter,
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === "dark";
  const [internalEvents, setInternalEvents] = useState<CalendarEvent[]>(events);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<"month" | "week">(defaultView);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  useEffect(() => setInternalEvents(events), [events]);


  const dayPropGetter = (date: Date) => {
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const isPast = date < new Date(today.setHours(0, 0, 0, 0));
    const isWeekend = [0, 6].includes(date.getDay());

    return {
      className: `
      ${isToday ? 'day-today' : ''}
      ${isPast && !isToday ? 'day-past' : ''}
      ${isWeekend ? 'day-weekend' : ''}
    `
    };
  };

  const handleSaveAppointment = (data: AppointmentData) => {
    const start = combineDayjsToDate(data.startTime);
    const end = combineDayjsToDate(data.endTime);

    const newOrUpdatedEvent: CalendarEvent = {
      id: data.id || Date.now().toString(),
      title: data.appointmentType || "Appointment",
      start,
      end,
      eventType: data.appointmentType,
      caseId: data.caseId,
      facilitator: data.facilitator,
      putativeParent: data.putativeParent,
      participants: data.participants,
      meetingMethod: data.meetingMethod,
      labName: data.labName,
      hearingType: data.hearingType,
      judge: data.judge,
      courtAddress: data.courtAddress,
      courtCity: data.courtCity,
      courtState: data.courtState,
      courtRoom: data.courtRoom,
      notes: data.notes,
    };

    setInternalEvents((prev) =>
      prev.some((e) => e.id === newOrUpdatedEvent.id)
        ? prev.map((e) => (e.id === newOrUpdatedEvent.id ? newOrUpdatedEvent : e))
        : [...prev, newOrUpdatedEvent]
    );

    onAppointmentSave?.(data);

    setIsDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleEventDrop = ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
    setInternalEvents((prev) =>
      prev.map((e) =>
        e.id === event.id
          ? { ...e, start: start instanceof Date ? start : new Date(start), end: end instanceof Date ? end : new Date(end) }
          : e
      )
    );
  };

  const handleNavigate = (direction: "prev" | "next" | "today") => {
    if (direction === "today") return setCurrentDate(new Date());
    const offset = direction === "next" ? 1 : -1;
    const newDate =
      currentView === "month"
        ? new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)
        : new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7 * offset);
    setCurrentDate(newDate);
  };

  const eventPropGetter = (event: CalendarEvent) => {
    let bg = "#3b82f6";
    switch (event.eventType) {
      case "Facilitation meeting":
        bg = "#0ea5e9";
        break;
      case "Genetic test":
        bg = "#f97316";
        break;
      case "Establishment hearing":
        bg = "#16a34a";
        break;
      case "Modification hearing":
        bg = "#9333ea";
        break;
    }

    const defaultStyle: React.CSSProperties = { backgroundColor: bg, borderRadius: 8, color: "white", padding: "6px 8px" };

    if (externalEventPropGetter) {
      const externalRes = externalEventPropGetter(event);
      return {
        ...externalRes,
        style: {
          ...defaultStyle,
          // If external result provides a className, we should probably clear the default background 
          // to let the className handle it, OR let externalRes.style override it.
          ...(externalRes.className ? { backgroundColor: undefined } : {}),
          ...externalRes.style
        }
      };
    }

    return { style: defaultStyle };
  };

  return (
    <div className={`scheduler-root${isDark ? " scheduler-dark" : ""}`}>
      <div className="rbc-toolbar-custom">
        <div className="toolbar-left">
          <CalendarIconTrigger currentDate={currentDate} setCurrentDate={setCurrentDate} />

          <button className="today-btn" onClick={() => handleNavigate("today")}>TODAY</button>


  <button className="nav-btn" aria-label="Previous" onClick={() => handleNavigate("prev")}><ChevronLeft /></button>
  <button className="nav-btn" aria-label="Next" onClick={() => handleNavigate("next")}><ChevronRight /></button>
  <span className="month-label">{format(currentDate, "MMMM yyyy")}</span>
</div>


        <div className="toolbar-right">
          <div className="view-toggle">
            <button className={`toggle-btn ${currentView === "month" ? "active" : ""}`} onClick={() => setCurrentView("month")}>Month</button>
            <button className={`toggle-btn ${currentView === "week" ? "active" : ""}`} onClick={() => setCurrentView("week")}>Week</button>
          </div>
          <button className="add-btn" onClick={() => { setSelectedEvent(null); setIsDialogOpen(true); }}>
            <Add /> ADD APPOINTMENT
          </button>
        </div>
      </div>

      <div className="calendar-wrap">
        <DragAndDropCalendar
          dayPropGetter={dayPropGetter}
          localizer={localizer}
          events={internalEvents}
          date={currentDate}
          view={currentView}
          onView={(v) => setCurrentView(v as "month" | "week")}
          onNavigate={(date) => setCurrentDate(date)}
          views={[Views.MONTH, Views.WEEK]}
          selectable={selectable}
          resizable
          popup
          onEventDrop={handleEventDrop}
          onSelectEvent={(event) => { setSelectedEvent(event); setIsDialogOpen(true); }}
          eventPropGetter={eventPropGetter}
          step={30}
          timeslots={2}
          style={{ height: "calc(100vh - 160px)" }}
        />
      </div>

      {AppointmentDialogComponent && (
        <AppointmentDialogComponent
          open={isDialogOpen}
          onClose={() => { setIsDialogOpen(false); setSelectedEvent(null); }}
          onSave={handleSaveAppointment}
          initialData={mapCalendarEventToAppointment(selectedEvent)}
        />
      )}
    </div>
  );
};

export default Schedular;
